// src/constants/reports.ts
export const REPORT_TYPE = {
  FLAT: 'FLAT',
  MULTI_PAGE: 'MULTI_PAGE',
} as const;

export const DEFAULT_REPORT_PROMPT = 'Here\'s the report';

// Empty-state copy for the Report Studio chat — a report-bound `/ui/chat`
// thread opened via "Customize in Studio". Replaces HomePage's home
// time-greeting so the surface reads as a focused report-editing workspace
// rather than a fresh lobby (the canvas, not the greeting, is the hero).
export const STUDIO_EMPTY_STATE_TITLE = "Let's get cooking!";
export const STUDIO_EMPTY_STATE_SUBTITLE =
  'Ask questions or describe edits to start customizing this artifact.';
export const STUDIO_INPUT_PLACEHOLDER =
  'Ask about this report or describe an edit…';

// Skill short_name for the report builder. The report-builder chat agent
// loads this via the `skills_get_skill` tool when a user message starts with
// `/report_builder`. The Reports create-form and the Report Studio chat
// both prefix the first user message with this command so the skill is in
// context for the rest of the session.
export const REPORT_BUILDER_SKILL_SHORT_NAME = 'report_builder';

/** Max characters of the prompt kept as a report title before truncating —
 *  matches the chat-history thread-title cap so Reports-list rows read the
 *  same as conversation titles. */
const REPORT_TITLE_MAX_LENGTH = 40;

/**
 * Derives a report display name from the first message of a slash-command
 * report build (`/report_builder <prompt>`). Strips the skill prefix, trims,
 * and truncates with an ellipsis the same way chat-thread titles do. This is
 * a stopgap label — the user's prompt is a description, not a true title — so
 * a dedicated rename remains the longer-term fix.
 */
export function deriveReportTitle(messageText: string): string {
  const stripped = messageText
    .replace(new RegExp(`^/${REPORT_BUILDER_SKILL_SHORT_NAME}\\b`), '')
    .trim();
  if (!stripped) return 'Untitled report';
  return stripped.length > REPORT_TITLE_MAX_LENGTH
    ? `${stripped.slice(0, REPORT_TITLE_MAX_LENGTH)}...`
    : stripped;
}

// Minimal HTML scaffold the studio creates up-front so the insight exists
// before the chat agent writes its first real revision. Keeping the bootstrap
// template here (vs server-side) means we only re-deploy the scaffold when the
// shell contract changes — the server stays a thin storage layer.
//
// The shell carries the contract the `report_builder` skill depends on:
//   - Tailwind Play CDN for utility classes.
//   - `strivelabs-report-theme.js` — encodes the brand (orange #F97316 as the
//     `brand` color, Inter as `font-sans`, --brand CSS variables for Chart.js
//     to resolve at chart-init time). One file = one source of truth for
//     brand and font; the LLM does not have to memorise either.
//   - Chart.js for visualisations, Lucide for iconography, and the
//     Strivelabs Report SDK (`window.dataset.query`, `window.app.execute`).
//
// The agent receives this template via `read_file` on its first turn and
// rewrites it wholesale, so the "Initializing your report…" splash is only
// visible during the brief window between insight creation and the agent's
// first `update_file`.
export const NEW_REPORT_HTML_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Untitled report</title>

<!-- Tailwind CDN -->
<script src="https://cdn.tailwindcss.com"></script>

<!-- Strivelabs report theme: brand color, Inter, tailwind.config, --brand CSS vars -->
<script src="https://assets.apps.strivelabs.co/app/platform/strivelabs-report-theme.js"></script>

<!-- Chart.js, Lucide icons, Strivelabs Report SDK -->
<script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.2/dist/chart.umd.min.js"></script>
<script src="https://unpkg.com/lucide@latest/dist/umd/lucide.min.js"></script>
<script src="https://assets.apps.strivelabs.co/app/platform/strivelabs-report-sdk.js"></script>

<style>
  /* Soft brand-gradient blob — a large radial gradient blurred to a soft
     edge so it reads as a halo rather than a hard disc. Inline because
     Tailwind cannot express radial-gradient + blur cleanly. */
  .sl-blob {
    width: 360px;
    height: 360px;
    border-radius: 9999px;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(249, 115, 22, 0.85) 0%,
      rgba(246, 165, 9, 0.7) 35%,
      rgba(249, 219, 207, 0.4) 60%,
      rgba(255, 255, 255, 0) 75%
    );
    filter: blur(28px);
  }
  @media (prefers-reduced-motion: no-preference) {
    .sl-blob { animation: sl-blob-pulse 4s ease-in-out infinite; }
    @keyframes sl-blob-pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50%      { transform: scale(1.04); opacity: 0.92; }
    }
  }
</style>
</head>
<body class="bg-white font-sans text-slate-900">
<main class="min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center" role="status" aria-live="polite">
  <div class="sl-blob mb-4" aria-hidden="true"></div>
  <h1 class="text-2xl font-semibold tracking-tight text-slate-900">Strive Labs</h1>
  <p class="mt-2 text-sm text-slate-500">Initializing your report…</p>
</main>
</body>
</html>
`;

export interface ReportSuggestion {
  label: string;
  prompt: string;
}

export const REPORT_SUGGESTIONS: ReportSuggestion[] = [
  {
    label: 'Google Ads weekly recap',
    prompt: 'Build a weekly Google Ads performance recap with spend, clicks, conversions, CPA trends, and a table of the top 10 campaigns by spend.',
  },
  {
    label: 'Conversion funnel',
    prompt: 'Show our marketing-to-sales funnel: visits, signups, qualified leads, opportunities, and closed-won, with conversion rates between each stage for the last 30 days.',
  },
  {
    label: 'Pipeline by source',
    prompt: 'Summarise current open pipeline grouped by lead source — count, total ARR, and average deal size per source — with a bar chart of ARR.',
  },
];

// Slot container animation and styling constants
export const SLOT_ANIMATION = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 },
} as const;

export const SLOT_CONTAINER_CLASSES =
  'mt-3 relative rounded-2xl border border-black/10 bg-white overflow-hidden';